import { BaseComponent } from 'src/app/common/components/base.component';
import { VisitorEvent } from './../../models/visitor.event';
import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { VisitorService } from '../../services/visitor.service';
// import ShareDialogParams = facebook.ShareDialogParams;
import { ConfigService } from 'src/app/common/services/config/config.service';
import { ShareService } from '../../services/share.service';
import { MediaQueryService } from 'src/app/common/services/media.query/media.query.service';

@Component({
  selector: 'ig-social-share',
  templateUrl: './social.share.component.html',
  styleUrls: ['./social.share.component.scss'],
})
export class SocialShareComponent extends BaseComponent implements OnInit {
  @Input() public item: VisitorEvent;
  @Input() public appearance: 'normal' | 'white' | 'rounded' = 'normal';
  @Input() public showTip = true;
  public link = '';
  public type = null;
  @Output() onShareToFB: EventEmitter<any> = new EventEmitter();
  @Output() onShareToTweet: EventEmitter<any> = new EventEmitter();
  @Output() onShareEmail: EventEmitter<any> = new EventEmitter();
  @Output() onGetLink: EventEmitter<any> = new EventEmitter();

  isMobile = false;

  constructor(
    public activatedRoute: ActivatedRoute,
    private _eventService: VisitorService,
    private _shareService: ShareService,
    private _mediaQueryService: MediaQueryService
  ) {
    super({ activatedRoute });

    this._mediaQueryService.isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile;
    });
  }

  ngOnInit() {
    this.setItemInfo();
    this.initFB();
  }

  public onKeyPress($event, social) {
    if ($event && $event.key === 'Enter') {
      if (social === 'facebook') {
        this.shareToFacebookMobileSimple();
      } else if (social === 'tweet') {
        this.tweetEvent();
      } else if (social === 'email') {
        this.mailEvent();
      }
    }
  }
public isMobileBrowser(): boolean {
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
}

/** Detect Facebook / Messenger / Instagram in-app web-views */
public isFBInApp(): boolean {
  //   FBAV = Facebook          FBAN = Messenger
  //   FB_IAB = Instagram IAB   ; see https://developers.facebook.com/docs/sharing/web
  return /(FBAN|FBAV|FB_IAB)/i.test(navigator.userAgent);
}

/** Detect if running on desktop browser (not mobile, not in-app) */
public isDesktopBrowser(): boolean {
  return !this.isMobileBrowser() && !this.isFBInApp();
}

public shareToFacebook(): void {
  this.setItemInfo();

  /* Build your OG page URL */
  const backendUrl =
    'https://api.imgoingcalendar.com/api/common/html';

  const isDesktop = this.isDesktopBrowser();
  const isMobileOrInApp = this.isMobileBrowser() || this.isFBInApp();

  if (isMobileOrInApp) {
    // Mobile/In-app flow: use payload encoding
    this.shareToFacebookMobileApp(backendUrl);
    return;
  }

  // Desktop flow: use plain query parameters
  const params = new URLSearchParams({
    type: this.type,
    id: this.item._id,
    hubName: this.hubName,
    url: window.location.href,
    name: this.item.name,
    ts: Date.now().toString(),
  });
  const shareUrl = `${backendUrl}?${params.toString()}`;

  // Pre-warm the URL so Facebook can crawl it
  this.preWarmUrl(shareUrl).then(() => {
    /* Regular Facebook share dialog */
    const fbShareUrl =
      'https://www.facebook.com/sharer/sharer.php' +
      `?u=${encodeURIComponent(shareUrl)}` +
      `&quote=${encodeURIComponent(
        `Check out ${this.item.name} on ${this.hubName}!`
      )}`;

    if (isDesktop) {
      /* Desktop flow: Login-first URL for desktop browsers only */
      const loginAndShare =
        'https://www.facebook.com/login.php' +
        `?next=${encodeURIComponent(fbShareUrl)}` +
        '&display=popup';

      /* Open pop-up; browser click-initiated => not blocked */
      const win = window.open(
        loginAndShare,
        'fb-login-share',
        'width=800,height=700,menubar=no,toolbar=no,status=no'
      );

      /* Fallback when pop-ups are blocked */
      if (!win) {
        window.location.href = loginAndShare;
      }
    } else {
      /* Mobile browser: Direct share without login requirement */
      window.open(fbShareUrl);
    }

    this.onShareToFB.emit();
  });
}

/**
 * Pre-warm the URL and force Facebook to refresh its cache
 */
private async preWarmUrl(url: string): Promise<void> {
  try {
    // Make a HEAD request to the URL to warm it up
    await fetch(url, { 
      method: 'HEAD',
      mode: 'no-cors' // Avoid CORS issues
    });
    
    // Force Facebook to refresh its cache of the URL
    await this.refreshFacebookCache(url);
    
    // Small delay to ensure everything is processed
    await new Promise(resolve => setTimeout(resolve, 800));
  } catch (error) {
    console.warn('Failed to pre-warm URL:', error);
    // Continue anyway after a small delay
    await new Promise(resolve => setTimeout(resolve, 500));
  }
}

/**
 * Force Facebook to refresh its cache of the shared URL
 */
private async refreshFacebookCache(url: string): Promise<void> {
  try {
    // Use Facebook's debug API to refresh the cache
    const debugUrl = `https://developers.facebook.com/tools/debug/clear-cache/?q=${encodeURIComponent(url)}`;
    
    // Make a request to Facebook's scraper to refresh the cache
    await fetch(`https://graph.facebook.com/?id=${encodeURIComponent(url)}&scrape=true`, {
      method: 'POST',
      mode: 'no-cors'
    }).catch(() => {
      // Fallback: use the debug URL approach
      const img = new Image();
      img.src = `https://graph.facebook.com/v12.0/?id=${encodeURIComponent(url)}&scrape=true&access_token=dummy`;
    });
    
  } catch (error) {
    console.warn('Failed to refresh Facebook cache:', error);
  }
}

public async shareToFacebookMobileApp(backendUrl: string): Promise<void> {
  const payload = {
    t: this.type,
    i: this.item._id,
    h: this.hubName,
    u: window.location.href,
    n: this.item.name,
    ts: Date.now(),
  };

  /* Base64-encode the JSON (URL-safe) */
  const encoded = btoa(JSON.stringify(payload))
    .replace(/\+/g, '-') // URL-safe
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  const shareUrl = `${backendUrl}?payload=${encoded}`;
  
  // Pre-warm the URL for mobile/in-app as well
  await this.preWarmUrl(shareUrl);
  
  // For mobile browsers, handle login state properly
  if (this.isMobileBrowser() && !this.isFBInApp()) {
    // Mobile browser - need to handle login state
    this.handleMobileBrowserShare(shareUrl);
  } else {
    // Facebook in-app browser - direct share
    const fbAppShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(fbAppShareUrl, '_blank');
  }
  
  this.onShareToFB.emit();
}

/**
 * Handle mobile browser sharing with proper login detection
 */
private handleMobileBrowserShare(shareUrl: string): void {
  // Create a unique identifier for this share session
  const shareSessionId = `fb_share_${Date.now()}`;
  
  // Store share URL in sessionStorage for after login
  try {
    sessionStorage.setItem(shareSessionId, shareUrl);
  } catch (e) {
    console.warn('SessionStorage not available');
  }

  // Try direct share first
  const fbShareUrl = `https://m.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  
  // Create a test window to check if share works
  const testWin = window.open(fbShareUrl, '_blank');
  
  if (!testWin) {
    // Popup blocked - fallback to current window
    this.fallbackMobileShare(shareUrl, shareSessionId);
    return;
  }

  // Monitor the test window
  const checkClosed = setInterval(() => {
    try {
      if (testWin.closed) {
        clearInterval(checkClosed);
        // Window closed - check if we need to handle login
        this.checkAndHandleLoginState(shareUrl, shareSessionId);
      }
    } catch (e) {
      // Cross-origin error - window is probably on Facebook domain
      clearInterval(checkClosed);
    }
  }, 1000);

  // Clean up after 30 seconds
  setTimeout(() => {
    clearInterval(checkClosed);
    if (!testWin.closed) {
      // Share is probably working
      try {
        sessionStorage.removeItem(shareSessionId);
      } catch (e) {}
    }
  }, 30000);
}

/**
 * Check login state and handle accordingly
 */
private checkAndHandleLoginState(shareUrl: string, sessionId: string): void {
  // Create a login-then-share URL
  const loginThenShareUrl = `https://m.facebook.com/login.php?next=${encodeURIComponent(
    `https://m.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
  )}`;

  // Try the login flow
  const loginWin = window.open(loginThenShareUrl, '_blank');
  
  if (!loginWin) {
    // Popup blocked - use current window
    window.location.href = loginThenShareUrl;
    return;
  }

  // Monitor login window
  const checkLoginClosed = setInterval(() => {
    try {
      if (loginWin.closed) {
        clearInterval(checkLoginClosed);
        // Clean up session storage
        try {
          sessionStorage.removeItem(sessionId);
        } catch (e) {}
      }
    } catch (e) {
      clearInterval(checkLoginClosed);
    }
  }, 1000);

  // Clean up after 60 seconds
  setTimeout(() => {
    clearInterval(checkLoginClosed);
  }, 60000);
}

/**
 * Fallback for mobile share when popups are blocked
 */
private fallbackMobileShare(shareUrl: string, sessionId: string): void {
  const userChoice = confirm(
    'To share on Facebook, you may need to log in first. Would you like to continue?'
  );
  
  if (userChoice) {
    // User confirmed - redirect to login-then-share
    const loginThenShareUrl = `https://m.facebook.com/login.php?next=${encodeURIComponent(
      `https://m.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    )}`;
    
    window.location.href = loginThenShareUrl;
  } else {
    // User cancelled - clean up
    try {
      sessionStorage.removeItem(sessionId);
    } catch (e) {}
  }
}

/**
 * Simplified and more reliable mobile sharing approach
 */
public shareToFacebookMobileSimple(): void {
  this.setItemInfo();

  const backendUrl = 'https://api.imgoingcalendar.com/api/common/html';
  
  let shareUrl: string;
  
  if (this.isMobileBrowser() || this.isFBInApp()) {
    // Mobile flow with payload
    const payload = {
      t: this.type,
      i: this.item._id,
      h: this.hubName,
      u: window.location.href,
      n: this.item.name,
      ts: Date.now(),
    };

    const encoded = btoa(JSON.stringify(payload))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    shareUrl = `${backendUrl}?payload=${encoded}`;
  } else {
    // Desktop flow
    const params = new URLSearchParams({
      type: this.type,
      id: this.item._id,
      hubName: this.hubName,
      url: window.location.href,
      name: this.item.name,
      ts: Date.now().toString(),
    });
    shareUrl = `${backendUrl}?${params.toString()}`;
  }

  // Check if user is likely logged into Facebook
  this.checkFacebookLoginAndShare(shareUrl);
}

/**
 * Check Facebook login state and share accordingly
 */
private checkFacebookLoginAndShare(shareUrl: string): void {
  // Try to detect if user is logged into Facebook by checking for Facebook cookies
  const hasFacebookCookies = document.cookie.includes('c_user=') || 
                            document.cookie.includes('xs=') ||
                            document.cookie.includes('sb=');

  const isMobile = this.isMobileBrowser() || this.isFBInApp();
  
  if (isMobile) {
    // Mobile approach - always try direct share first
    const directShareUrl = `https://m.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    
    // Try opening in a popup first
    const popup = window.open(directShareUrl, 'fbshare', 'width=600,height=400');
    
    if (!popup) {
      // Popup blocked - ask user and redirect in current window
      const proceed = confirm('To share on Facebook, we need to redirect you. Continue?');
      if (proceed) {
        window.location.href = directShareUrl;
      }
    } else {
      // Popup opened - monitor it
      const checkPopup = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(checkPopup);
            // If popup closed quickly, might be a login issue
            // But don't do anything - let user retry if needed
          }
        } catch (e) {
          clearInterval(checkPopup);
        }
      }, 1000);
      
      // Clean up after 30 seconds
      setTimeout(() => clearInterval(checkPopup), 30000);
    }
  } else {
    // Desktop approach - use login-first for desktop
    const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    const loginAndShare = `https://www.facebook.com/login.php?next=${encodeURIComponent(fbShareUrl)}&display=popup`;
    
    const win = window.open(loginAndShare, 'fb-login-share', 'width=800,height=700,menubar=no,toolbar=no,status=no');
    
    if (!win) {
      window.location.href = loginAndShare;
    }
  }
  
  this.onShareToFB.emit();
}

  public tweetEvent() {
    this.setItemInfo();
    const shareLink = ConfigService.config.eventShare.tweet
      .replace('${text}', this.item.name)
      .replace('${url}', encodeURIComponent(this.link));
    this.popupCenter(shareLink);

    this.onShareToTweet.emit();
  }

  public mailEvent() {
    this.setItemInfo();
    const content =
      this._eventService.getItemType(this.item) === 'event'
        ? this._shareService.toMailableEvent(
            this.item.hubName,
            this.item,
            window.location.href
          )
        : this._shareService.toMailablePlace(
            this.item.hubName,
            this.item,
            window.location.href
          );
    if (this.isMobile) {
      window.open(content);
    } else {
      window.location.href = content;
    }

    this.onShareEmail.emit();
  }

  public getLink() {
    this.onGetLink.emit();
  }

  private setItemInfo() {
    if (this._eventService.getItemType(this.item) === 'event') {
      this.type = 'event';
      this.link = this.item.eventLink || this.item.hostLink;
    } else {
      this.type = 'place';
      // place
    }
    // this.link = this.item.startTime
    //   ? this.item.eventLink
    //   : this.item.hostLink ||
    //     (this.item as HubPlace).hostGoogleLink ||
    //     this.item.hostFacebookLink;
    this.link = this.link || window.location.href;
  }

  private popupCenter(url, title = '', width = 575, height = 400) {
    const dualScreenLeft = window.screenLeft;
    const dualScreenTop = window.screenTop;

    const windowWidth = window.innerWidth
      ? window.innerWidth
      : document.documentElement.clientWidth
      ? document.documentElement.clientWidth
      : screen.width;
    const windowHeight = window.innerHeight
      ? window.innerHeight
      : document.documentElement.clientHeight
      ? document.documentElement.clientHeight
      : screen.height;

    const left = windowWidth / 2 - width / 2 + dualScreenLeft;
    const top = windowHeight / 2 - height / 2 + dualScreenTop;
    const newWindow = window.open(
      url,
      title,
      `scrollbars=yes, width=${width}, height=${height}, top=${top}, left=${left}`
    );

    if (window.focus) {
      newWindow.focus();
    }
  }

  private initFB() {
    window['FB'].init(ConfigService.config.facebook.sdkConfig);
  }
}
